import httpService from '../httpService';
import { store } from '../../redux/store';
import {
  addBusiness,
  deleteBusiness,
  setBusinessDescription,
  setBusinessFeatures,
  setBusinessName,
  setBusinessPicture,
  setSelectedBusiness,
  setSelectedDashboard,
  setState,
  wipeSelectedBusiness,
  addCommunity,
} from '../../redux/slices/applicationSlice';
import { CreateBusinessForm } from '../../types/forms';
import Business, { BusinessType } from '../../types/business';
import { BusinessFeatures } from '../../utils/FeatureUtils';
import Community, { CommunityType } from '../../types/community';

const businessService = {
  /**
   * Sets the business profile image.
   *
   * @function
   * @async
   * @param {File} image - The image file to set as the business profile image.
   * @returns {Promise<void>} A Promise that resolves when the profile image has been set.
   */
  setProfileImage: async (image: File): Promise<void> => {
    const businessId = store.getState().application.selectedBusiness;
    const formData = new FormData();
    formData.append('avatar', image);
    return httpService
      .post(`/networks/businesses/${businessId}/profile-picture`, formData)
      .then(({ data }): void => {
        store.dispatch(setBusinessPicture(data.path));
      });
  },

  /**
   * Deletes the business profile image.
   *
   * @function
   * @async
   * @returns {Promise<void>} A Promise that resolves when the profile image has been deleted.
   */
  deleteProfileImage: async (): Promise<void> => {
    const businessId = store.getState().application.selectedBusiness;
    return httpService
      .delete(`/networks/businesses/${businessId}/profile-picture`)
      .then((): void => {
        store.dispatch(setBusinessPicture(null));
        window.dispatchEvent(new Event('profileImage'));
      });
  },

  /**
   * Creates a new business.
   *
   * @function
   * @async
   * @param {CreateBusinessForm} businessForm - The form data for creating a business.
   * @returns {Promise<void>} A Promise that resolves when the business has been created.
   */
  createBusiness: async (businessForm: CreateBusinessForm): Promise<void> => {
    const community = 
      businessForm.type === BusinessType.SPORTCLUB ? CommunityType.SPORTTEAM : 
      businessForm.type === BusinessType.STUDENTASSOCIATION ? CommunityType.ASSOCIATION :
      CommunityType.OTHER_COMMUNITY;
    
    businessForm = {
        ...businessForm,
        community: {
          name: businessForm.name,
          description: businessForm.description || '',
          enableDashboard: true,
          features: businessForm.dashboardFeatures,
          type: community,
        }
    };

    const { data: business } = await httpService.post<Business>(
      '/networks/businesses',
      businessForm,
      {
        params: {
          origin: 'business-dashboard',
        },
      },
    );

    store.dispatch(addBusiness(business));
    if (business.businessId) store.dispatch(setSelectedBusiness(business.businessId));
    if (business.dashboard?.dashboardId)
      store.dispatch(setSelectedDashboard(business.dashboard?.dashboardId));
  },

  /**
   * Fetches all businesses for the current user.
   *
   * @function
   * @async
   * @returns {Promise<void>} A Promise that resolves when the businesses have been fetched.
   */
  fetchBusinesses: async (): Promise<void> => {
    return httpService.get<Business[]>('/networks/businesses').then(({ data }): void => {
      
      const existingCommunityIds = store.getState().application.communities?.map((c: Community) => c.id) || [];
      data.forEach((business) => {
        if (business.community && !existingCommunityIds.includes(business.community.id)) {
          store.dispatch(addCommunity(business.community));
          existingCommunityIds.push(business.community.id);
        }
      });

      store.dispatch(setState({ ...store.getState().application, businesses: data }));
    });
  },

  /**
   * Deletes the current business.
   *
   * @function
   * @async
   * @returns {Promise<void>} A Promise that resolves when the business has been deleted.
   */
  deleteBusiness: async (): Promise<void> => {
    const businessId = store.getState().application.selectedBusiness;
    await httpService.delete(`/networks/businesses/${businessId}`);
    store.dispatch(wipeSelectedBusiness());
    if (businessId) store.dispatch(deleteBusiness(businessId));
  },

  /**
   * Updates the current business.
   *
   * @function
   * @async
   * @param {string} name - The new name for the business.
   * @param {string} description - The new description for the business.
   * @param {BusinessFeatures} features - The features to enable for the business.
   * @returns {Promise<void>} A Promise that resolves when the business has been updated.
   */
  updateBusiness: async (
    name: string,
    description: string,
    features: BusinessFeatures,
  ): Promise<void> => {
    const businessId = store.getState().application.selectedBusiness;
    await httpService.put(`/networks/businesses/${businessId}`, {
      name,
      description,
      communityFeatures: features,
      dashboardFeatures: features,
    });
    store.dispatch(setBusinessName(name));
    store.dispatch(setBusinessDescription(description));
    store.dispatch(setBusinessFeatures(features));
  },

  /**
   * Gets the business by ID.
   *
   * @function
   * @async
   * @param {number} businessId - The ID of the business to fetch.
   * @returns {Promise<Business>} A Promise that resolves with the business data.
   */
  getBusinessById: async (businessId: number): Promise<Business> => {
    const { data } = await httpService.get<Business>(`/networks/businesses/${businessId}`);
    return data;
  },

  updateSubCommunity: async (
    businessId: number,
    communityId: number,
    details: { name: string; description?: string },
  ): Promise<void> => {
    await httpService.put(`/networks/businesses/${businessId}/communities/${communityId}`, details);
  },

  deleteSubCommunity: async (businessId: number, communityId: number): Promise<void> => {
    await httpService.delete(`/networks/businesses/${businessId}/communities/${communityId}`);
  },

  connectExistingCommunity: async (
    businessId: number,
    communityId: number,
    subscriptionType: string,
  ): Promise<void> => {
    await httpService.post(`/networks/businesses/${businessId}/communities/${communityId}/connect`, {
      subscriptionType,
    });
  },

};

export default businessService;
